import { useEffect, useState } from 'react';
import type { Customer, Lang, Reservation } from './data/types';
import { DashboardProvider, useDashboard, useDay, useLogout } from './data/context';
import { addDays, todayRome } from './lib/dates';
import { t } from './i18n/t';
import { Sidebar } from './shell/Sidebar';
import { Topbar } from './shell/Topbar';
import type { CalView, Route } from './shell/routes';
import { CalendarView } from './views/CalendarView';
import { BookingsView } from './views/BookingsView';
import { CustomersView } from './views/CustomersView';
import { NotificationsView } from './views/NotificationsView';
import { InsightsView } from './views/InsightsView';
import { AvailabilityView } from './views/AvailabilityView';
import { TablesView } from './views/TablesView';
import { WebsiteView } from './views/WebsiteView';
import { SettingsView } from './views/SettingsView';
import { ResvDrawer } from './drawers/ResvDrawer';
import { CustomerDrawer } from './drawers/CustomerDrawer';
import { NewReservationModal } from './components/NewReservationModal';

/**
 * Owner-portal root.
 *
 * Wraps everything in a DashboardProvider, which loads data from the
 * API if `VITE_RISMA_API_URL` is set, otherwise falls back to the mock
 * fixtures. Views consume the data via `useDashboard()`.
 *
 * Two structural responsive bug fixes the spec is explicit about:
 *   1. `.app { display: flex }` (NOT a 2-col grid that collapses to 1fr)
 *   2. `.icon-btn.hamburger` class — matches `.icon-btn` specificity so
 *      hide/show rules win deterministically across breakpoints
 * Both live in global.css; we just have to use the right class names here.
 */
export function App() {
  return (
    <DashboardProvider>
      <Shell />
    </DashboardProvider>
  );
}

function Shell() {
  const { tenant, dateLabel, reservations, customers, notifications, isImpersonated, impersonatedBy } =
    useDashboard();
  const { day, setDay } = useDay();
  const logout = useLogout();

  // White-label: repaint the portal in the restaurant's own brand — the
  // accent (logo, active-nav, toggles, badges) and the dark sidebar surface.
  // No brand set ⇒ Risma's default palette stays.
  useEffect(() => {
    const root = document.documentElement;
    const vars = ['--terra', '--terra-deep', '--sidebar', '--sidebar-hi'];
    const b = tenant.brand;
    if (!b) {
      vars.forEach((v) => root.style.removeProperty(v));
      return;
    }
    root.style.setProperty('--terra', b.accent);
    root.style.setProperty('--terra-deep', `color-mix(in srgb, ${b.accent} 82%, #000)`);
    // Explicit sidebar wins; otherwise derive a dark tint of the accent so a
    // tenant with only `accent` set (the common case) still gets a branded nav.
    const sidebar = b.sidebar ?? `color-mix(in srgb, ${b.accent} 24%, #1d1712)`;
    root.style.setProperty('--sidebar', sidebar);
    root.style.setProperty('--sidebar-hi', `color-mix(in srgb, ${sidebar} 86%, #fff)`);
  }, [tenant.brand?.accent, tenant.brand?.sidebar]);

  const [lang, setLang] = useState<Lang>('it');
  const [route, setRoute] = useState<Route>(() => {
    // Honour the ?view=website deep-link from the Console's "Apri editor sito".
    try {
      return new URLSearchParams(window.location.search).get('view') === 'website' ? 'website' : 'cal';
    } catch {
      return 'cal';
    }
  });
  const [calView, setCalView] = useState<CalView>('timeline');
  const [sideOpen, setSideOpen] = useState(false);
  const [sideCollapsed, setSideCollapsed] = useState(() => {
    try {
      return localStorage.getItem('risma-side-collapsed') === '1';
    } catch {
      return false;
    }
  });
  const toggleCollapse = () =>
    setSideCollapsed((v) => {
      const next = !v;
      try {
        localStorage.setItem('risma-side-collapsed', next ? '1' : '0');
      } catch {
        /* ignore */
      }
      return next;
    });
  const [selectedResv, setSelectedResv] = useState<Reservation | null>(null);
  const [selectedCust, setSelectedCust] = useState<Customer | null>(null);
  const [newResvOpen, setNewResvOpen] = useState(false);

  const toggleLang = () => setLang((l) => (l === 'it' ? 'en' : 'it'));
  const go = (id: Route) => {
    setRoute(id);
    setSideOpen(false);
  };

  // Per-route title + sub. Derived from live data so counts stay accurate.
  const D = dateLabel[lang];
  const unread = notifications.filter((n) => !n.read).length;
  const newCount = reservations.filter((r) => r.status === 'new').length;

  const titles: Record<Route, { title: string; sub: string }> = {
    cal: { title: t('cal', lang), sub: `${D.dow} ${D.d} ${D.mon} ${D.y}` },
    bookings: {
      title: t('bookings', lang),
      sub: `${reservations.length} · ${newCount} ${t('pending', lang)}`,
    },
    customers: {
      title: t('customers', lang),
      sub: `${customers.length} ${t('customers', lang).toLowerCase()}`,
    },
    notifications: {
      title: t('notifications', lang),
      sub: `${unread} ${t('pending', lang).toLowerCase()}`,
    },
    insights: { title: t('insights', lang), sub: t('thisWeek', lang) },
    availability: { title: t('availability', lang), sub: tenant.name },
    tables: { title: t('tables', lang), sub: tenant.name },
    website: { title: t('website', lang), sub: tenant.name },
    settings: { title: t('settings', lang), sub: tenant.name },
  };

  return (
    <div className="app">
      <Sidebar
        lang={lang}
        route={route}
        onNavigate={go}
        open={sideOpen}
        onClose={() => setSideOpen(false)}
        collapsed={sideCollapsed}
        onToggleCollapse={toggleCollapse}
        tenant={tenant}
        notifications={notifications}
      />

      <div className="main">
        {isImpersonated && (
          <div className="imp-banner" role="status">
            <span>
              Stai modificando come <b>{tenant.name}</b>
              {impersonatedBy ? ` · accesso staff (${impersonatedBy.name ?? impersonatedBy.email})` : ' · accesso staff'}
            </span>
            {logout && (
              <button type="button" className="imp-exit" onClick={() => void logout()}>
                Esci dall’accesso
              </button>
            )}
          </div>
        )}
        <Topbar
          lang={lang}
          onToggleLang={toggleLang}
          route={route}
          onOpenSidebar={() => setSideOpen(true)}
          onGotoNotifications={() => go('notifications')}
          onNewReservation={() => setNewResvOpen(true)}
          calView={calView}
          onCalView={setCalView}
          title={titles[route].title}
          sub={titles[route].sub}
          dateLabel={dateLabel}
          notifications={notifications}
          day={day}
          onPrevDay={() => setDay((d) => addDays(d, -1))}
          onNextDay={() => setDay((d) => addDays(d, 1))}
          onToday={() => setDay(todayRome())}
          onPickDay={setDay}
        />

        {route === 'cal' && (
          <CalendarView lang={lang} view={calView} onOpen={setSelectedResv} />
        )}
        {route === 'bookings' && <BookingsView lang={lang} onOpen={setSelectedResv} />}
        {route === 'customers' && <CustomersView lang={lang} onOpen={setSelectedCust} />}
        {route === 'notifications' && <NotificationsView lang={lang} />}
        {route === 'insights' && (
          <InsightsView lang={lang} onOpenCustomer={setSelectedCust} />
        )}
        {route === 'availability' && <AvailabilityView lang={lang} onNavigate={go} />}
        {route === 'tables' && <TablesView lang={lang} />}
        {route === 'website' && <WebsiteView lang={lang} />}
        {route === 'settings' && <SettingsView lang={lang} />}
      </div>

      <ResvDrawer resv={selectedResv} lang={lang} onClose={() => setSelectedResv(null)} />
      <CustomerDrawer cust={selectedCust} lang={lang} onClose={() => setSelectedCust(null)} />
      <NewReservationModal open={newResvOpen} onClose={() => setNewResvOpen(false)} />
    </div>
  );
}
